import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import AppHeader from '../components/AppHeader';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2, Coins, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const { isDark } = useTheme();

  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;
  const pollInterval = 2000;

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!sessionId) {
      setStatus('error');
      return;
    }

    pollPaymentStatus();
  }, [sessionId, isAuthenticated]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(data);
        toast.success('Payment successful!');
        // Reload to update user credits
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 3000);
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);

    } catch (error) {
      console.error('Payment status error:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  const successRightContent = isAuthenticated ? (
    <Button
      variant="ghost"
      onClick={() => navigate('/dashboard')}
      className="font-semibold rounded-xl"
      style={{ color: isDark ? '#c9d1d9' : '#475569' }}
      data-testid="nav-dashboard"
    >
      Dashboard
    </Button>
  ) : null;

  return (
    <div
      className="min-h-screen flex flex-col transition-colors duration-300 page-enter"
      style={{ background: isDark ? '#0a0f1e' : '#f8fafc' }}
    >
      <AppHeader rightContent={successRightContent} />

      <div className="flex-1 flex items-center justify-center p-6">
        <div
          className="max-w-md w-full border p-8 text-center rounded-2xl shadow-sm transition-colors duration-300"
          style={{
            background: isDark ? '#0f1729' : '#ffffff',
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
          }}
        >
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 text-[#F87217] mx-auto mb-6 animate-spin" />
              <h1
                className="text-2xl font-bold font-['Outfit'] mb-2 transition-colors duration-300"
                style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}
              >
                Processing Payment
              </h1>
              <p
                className="transition-colors duration-300 mb-6"
                style={{ color: isDark ? '#8b949e' : '#64748b' }}
              >
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
              <h1
                className="text-2xl font-bold font-['Outfit'] mb-2 transition-colors duration-300"
                style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}
              >
                Payment Successful!
              </h1>
              <p
                className="transition-colors duration-300 mb-6"
                style={{ color: isDark ? '#8b949e' : '#64748b' }}
              >
                You've been upgraded to the <span className="font-semibold capitalize">{paymentData?.plan}</span> plan.
              </p>

              <div
                className="p-4 border rounded-xl mb-6 transition-colors duration-300"
                style={{
                  background: isDark ? 'rgba(16,185,129,0.1)' : '#f0fdf4',
                  borderColor: isDark ? 'rgba(16,185,129,0.2)' : '#bbf7d0'
                }}
              >
                <div className="flex items-center justify-center gap-2">
                  <Coins className="w-5 h-5 text-emerald-500" />
                  <span className="text-lg font-bold" style={{ color: isDark ? '#34d399' : '#15803d' }}>
                    {paymentData?.credits?.toLocaleString()} credits added
                  </span>
                </div>
              </div>

              <p
                className="text-sm transition-colors duration-300 mb-4"
                style={{ color: isDark ? '#6e7681' : '#94a3b8' }}
              >
                Redirecting to dashboard...
              </p>

              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full text-white font-semibold rounded-xl transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg,#F87217,#FF9A4E)',
                  boxShadow: '0 4px 16px rgba(248,114,23,0.25)',
                }}
              >
                Go to Dashboard <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </>
          )}

          {status === 'expired' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h1
                className="text-2xl font-bold font-['Outfit'] mb-2 transition-colors duration-300"
                style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}
              >
                Payment Expired
              </h1>
              <p
                className="transition-colors duration-300 mb-6"
                style={{ color: isDark ? '#8b949e' : '#64748b' }}
              >
                Your payment session has expired. Please try again.
              </p>
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full text-white font-semibold rounded-xl transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg,#F87217,#FF9A4E)',
                  boxShadow: '0 4px 16px rgba(248,114,23,0.25)',
                }}
              >
                Back to Pricing
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h1
                className="text-2xl font-bold font-['Outfit'] mb-2 transition-colors duration-300"
                style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}
              >
                Something Went Wrong
              </h1>
              <p
                className="transition-colors duration-300 mb-6"
                style={{ color: isDark ? '#8b949e' : '#64748b' }}
              >
                We couldn't process your payment. Please contact support.
              </p>
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full text-white font-semibold rounded-xl transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg,#F87217,#FF9A4E)',
                  boxShadow: '0 4px 16px rgba(248,114,23,0.25)',
                }}
              >
                Back to Pricing
              </Button>
            </>
          )}

          {status === 'timeout' && (
            <>
              <Loader2 className="w-16 h-16 text-yellow-500 mx-auto mb-6 animate-spin" />
              <h1
                className="text-2xl font-bold font-['Outfit'] mb-2 transition-colors duration-300"
                style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}
              >
                Payment Processing
              </h1>
              <p
                className="transition-colors duration-300 mb-6"
                style={{ color: isDark ? '#8b949e' : '#64748b' }}
              >
                Your payment is still being processed. Please check your email for confirmation.
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full text-white font-semibold rounded-xl transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg,#F87217,#FF9A4E)',
                  boxShadow: '0 4px 16px rgba(248,114,23,0.25)',
                }}
              >
                Go to Dashboard
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
